/**
 * Export Plan Group Deep Dive as standalone HTML or PDF.
 * The HTML export inlines a stylesheet so the file opens cleanly outside the app.
 * The PDF export uses the browser's native print-to-PDF via an offscreen iframe
 * — this produces high-quality, selectable-text PDFs with no canvas rasterization.
 */
import { formatDateAU } from './dateFormat';

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
};

const fmt = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
const fmtPct = (v, d = 1) => v == null ? '—' : `${Number(v).toFixed(d)}%`;
const fmtNum = (v, d = 1) => v == null ? '—' : Number(v).toFixed(d);

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #171717; background: #ffffff; margin: 0; padding: 32px; line-height: 1.5; }
  h1 { font-size: 22px; margin: 0 0 4px; letter-spacing: -0.02em; }
  h2 { font-size: 15px; margin: 0 0 12px; letter-spacing: -0.01em; }
  h3 { font-size: 12px; margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #525252; }
  .meta { color: #737373; font-size: 12px; margin-bottom: 24px; }
  .section { margin-bottom: 28px; page-break-inside: avoid; }
  .grid { display: grid; gap: 12px; }
  .grid-3 { grid-template-columns: repeat(3, 1fr); }
  .grid-2 { grid-template-columns: repeat(2, 1fr); }
  .card { border: 1px solid #E5E5E5; border-radius: 10px; padding: 14px; background: #fff; }
  .card-label { font-size: 10px; font-weight: 600; color: #A3A3A3; text-transform: uppercase; letter-spacing: 0.06em; }
  .card-value { font-size: 22px; font-weight: 700; margin-top: 4px; color: #171717; }
  .card-sub { font-size: 10px; color: #737373; margin-top: 2px; }
  .advisor { padding: 18px; border-radius: 12px; border: 2px solid #93c5fd; background: #eff6ff; color: #1e40af; margin-bottom: 24px; }
  .advisor.emerald, .advisor.amber, .advisor.orange, .advisor.red { background: #eff6ff; border-color: #93c5fd; color: #1e40af; }
  .advisor-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }
  .advisor h2 { font-size: 22px; }
  .swot { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .swot-quadrant { border: 1px solid; border-radius: 12px; padding: 14px; page-break-inside: avoid; }
  .swot-quadrant.s { background: rgba(16,185,129,0.05); border-color: #a7f3d0; }
  .swot-quadrant.s h3 { color: #047857; }
  .swot-quadrant.w { background: rgba(239,68,68,0.05); border-color: #fecaca; }
  .swot-quadrant.w h3 { color: #b91c1c; }
  .swot-quadrant.o { background: rgba(59,130,246,0.05); border-color: #bfdbfe; }
  .swot-quadrant.o h3 { color: #1d4ed8; }
  .swot-quadrant.t { background: rgba(245,158,11,0.05); border-color: #fde68a; }
  .swot-quadrant.t h3 { color: #b45309; }
  .swot-item { padding: 6px 0; border-bottom: 1px solid rgba(0,0,0,0.05); }
  .swot-item:last-child { border-bottom: none; }
  .swot-title { font-size: 11px; font-weight: 700; color: #171717; display: flex; justify-content: space-between; align-items: baseline; }
  .swot-metric { font-family: monospace; font-size: 10px; color: #525252; }
  .swot-detail { font-size: 11px; color: #525252; margin-top: 3px; line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  th { background: #FAFAFA; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #737373; border-bottom: 1px solid #E5E5E5; }
  td { padding: 8px 10px; border-bottom: 1px solid #F5F5F5; font-size: 11px; color: #525252; }
  td.right { text-align: right; font-family: monospace; }
  td.center { text-align: center; }
  tr.this-group { background: #FAFAFA; font-weight: 600; color: #171717; }
  .rank-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
  .rank-badge { padding: 5px 10px; border-radius: 6px; border: 1px solid; font-size: 11px; }
  .rank-badge.good { background: rgba(16,185,129,0.08); border-color: #a7f3d0; color: #047857; }
  .rank-badge.mid { background: rgba(245,158,11,0.08); border-color: #fde68a; color: #b45309; }
  .rank-badge.bad { background: rgba(239,68,68,0.08); border-color: #fecaca; color: #b91c1c; }
  .talking-list { list-style: none; padding: 0; margin: 6px 0 0; }
  .talking-list li { padding: 4px 0; font-size: 11px; }
  .talking-list li::before { content: '→ '; opacity: 0.6; font-weight: 600; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #E5E5E5; color: #A3A3A3; font-size: 10px; text-align: center; }
  .verbatim { padding: 8px 12px; border: 1px solid; border-radius: 8px; margin-bottom: 6px; font-size: 11px; font-style: italic; }
  .verbatim.p { background: rgba(16,185,129,0.04); border-color: #a7f3d0; }
  .verbatim.pa { background: rgba(245,158,11,0.04); border-color: #fde68a; }
  .verbatim.d { background: rgba(239,68,68,0.04); border-color: #fecaca; }
  .v-meta { display: flex; justify-content: space-between; font-family: monospace; font-size: 10px; color: #525252; font-style: normal; margin-bottom: 3px; }
  @media print {
    @page { size: A4; margin: 12mm; }
    body { padding: 0; }
    .section, .swot-quadrant, .advisor { page-break-inside: avoid; }
  }
`;

const rankClass = (rank, total) => {
  if (!rank || !total) return '';
  const pct = (rank / total) * 100;
  if (pct <= 25) return 'good';
  if (pct <= 75) return 'mid';
  return 'bad';
};

const buildHTMLDocument = (data) => {
  const { target, peers, peerStats, swot, renewal, npsComments, ranks, peerMode } = data;

  const swotHTML = (items, cls, title) => `
    <div class="swot-quadrant ${cls}">
      <h3>${title} <span style="float:right;color:#A3A3A3;font-weight:600;">${items.length}</span></h3>
      ${items.map(i => `
        <div class="swot-item">
          <div class="swot-title"><span>${i.title}</span>${i.metric && i.metric !== '—' ? `<span class="swot-metric">${i.metric}</span>` : ''}</div>
          <div class="swot-detail">${i.detail}</div>
        </div>
      `).join('')}
    </div>
  `;

  const peerRowsHTML = [
    `<tr class="this-group">
      <td>${target.basePlan}</td>
      <td>${target.propertyName || '—'} <span style="font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#737373;margin-left:4px;">(this group)</span></td>
      <td>${target.primaryManager}</td>
      <td class="right">${fmt(target.totalRevenue)}</td>
      <td class="right">${fmt(target.revenuePerLot)}</td>
      <td class="right">${fmtPct(target.margin, 1)}</td>
      <td class="right" ${target.profit < 0 ? 'style="color:#b91c1c;"' : ''}>${fmt(target.profit)}</td>
      <td class="right">${target.totalOccupiableLots || '—'}</td>
      <td class="center">${target.avgNPS != null ? fmtNum(target.avgNPS) : '—'}</td>
      <td class="center">${target.totalMeetings}</td>
    </tr>`,
    ...peers.map(p => `
      <tr>
        <td>${p.basePlan}</td>
        <td>${p.propertyName || '—'}</td>
        <td>${p.manager}</td>
        <td class="right">${fmt(p.totalRevenue)}</td>
        <td class="right">${fmt(p.revenuePerLot)}</td>
        <td class="right">${fmtPct(p.margin, 1)}</td>
        <td class="right" ${p.profit < 0 ? 'style="color:#b91c1c;"' : ''}>${fmt(p.profit)}</td>
        <td class="right">${p.totalOccupiableLots || '—'}</td>
        <td class="center">${p.avgNPS != null ? fmtNum(p.avgNPS) : '—'}</td>
        <td class="center">${p.totalMeetings}</td>
      </tr>
    `),
  ].join('');

  const expiryLine = renewal.expiryDate
    ? `Expires ${formatDateAU(renewal.expiryDate)}${renewal.daysToExpiry != null ? ` (${renewal.daysToExpiry > 0 ? `in ${renewal.daysToExpiry} days` : `${Math.abs(renewal.daysToExpiry)} days overdue`})` : ''}`
    : 'No expiry date on record';

  return `<!DOCTYPE html>
<html lang="en-AU">
<head>
<meta charset="utf-8">
<title>Plan Group Deep Dive — ${target.propertyName || target.basePlan}</title>
<style>${PRINT_CSS}</style>
</head>
<body>
  <div class="section">
    <h1>${target.propertyName || target.basePlan}</h1>
    <div class="meta">Plan Group Deep Dive · ${target.basePlan} · Generated ${formatDateAU(new Date())}</div>
    <div class="card">
      <div style="display:flex; justify-content:space-between; flex-wrap:wrap; gap:12px;">
        <div>
          <div style="font-size:12px;color:#525252;">${target.primaryManager} · ${target.planCount} plan${target.planCount > 1 ? 's' : ''} · ${target.totalOccupiableLots} lots · <strong>${target.dominantPersona}</strong></div>
        </div>
        <div class="rank-row">
          ${ranks.revenue.rank ? `<span class="rank-badge ${rankClass(ranks.revenue.rank, ranks.revenue.total)}">Revenue #${ranks.revenue.rank}/${ranks.revenue.total}</span>` : ''}
          ${ranks.margin.rank ? `<span class="rank-badge ${rankClass(ranks.margin.rank, ranks.margin.total)}">Margin #${ranks.margin.rank}/${ranks.margin.total}</span>` : ''}
          ${ranks.nps.rank ? `<span class="rank-badge ${rankClass(ranks.nps.rank, ranks.nps.total)}">NPS #${ranks.nps.rank}/${ranks.nps.total}</span>` : ''}
        </div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="advisor ${renewal.actionColor}">
      <div class="advisor-grid">
        <div>
          <h3 style="color:inherit;opacity:0.8;">Advisor Recommendation</h3>
          <h2 style="margin:4px 0 6px;font-size:22px;">${renewal.action}</h2>
          <div style="font-size:12px;line-height:1.5;">${renewal.actionSummary}</div>
        </div>
        <div>
          <h3 style="color:inherit;opacity:0.8;">Renewal Priority</h3>
          <div style="font-size:13px;font-weight:600;">${renewal.priority}</div>
          <div style="font-size:11px;opacity:0.85;margin-top:2px;">${expiryLine}</div>
          <h3 style="color:inherit;opacity:0.8;margin-top:12px;">Fee Guidance</h3>
          <div style="font-size:11px;line-height:1.5;">${renewal.feeGuidance}</div>
        </div>
        <div>
          <h3 style="color:inherit;opacity:0.8;">Talking Points</h3>
          <ul class="talking-list">${renewal.talkingPoints.map(t => `<li>${t}</li>`).join('')}</ul>
        </div>
      </div>
    </div>
  </div>

  <div class="section">
    <h2>Key Metrics</h2>
    <div class="grid grid-3">
      <div class="card"><div class="card-label">Revenue</div><div class="card-value">${fmt(target.totalRevenue)}</div><div class="card-sub">${target.totalOccupiableLots} lots</div></div>
      <div class="card"><div class="card-label">Revenue / Lot</div><div class="card-value">${fmt(target.revenuePerLot)}</div><div class="card-sub">fee intensity</div></div>
      <div class="card"><div class="card-label">Gross Margin</div><div class="card-value">${fmtPct(target.margin)}</div><div class="card-sub">${fmt(target.profit)} gross margin</div></div>
      <div class="card"><div class="card-label">Workload</div><div class="card-value">${fmt(target.totalWorkload)}</div><div class="card-sub">${fmt(target.workloadPerLot)} / lot</div></div>
      <div class="card"><div class="card-label">NPS</div><div class="card-value">${target.avgNPS != null ? fmtNum(target.avgNPS) : '—'}</div><div class="card-sub">${target.npsResponseCount > 0 ? `${target.promoters}P / ${target.passives}Pa / ${target.detractors}D` : 'no responses'}</div></div>
      <div class="card"><div class="card-label">Compliance</div><div class="card-value">${target.avgCompliance != null ? `${Math.round(target.avgCompliance)}%` : '—'}</div><div class="card-sub">quality score</div></div>
      <div class="card"><div class="card-label">Meetings</div><div class="card-value">${target.totalMeetings}</div><div class="card-sub">${target.comMeetings}C ${target.agmMeetings}A ${target.specialMeetings}S</div></div>
    </div>
  </div>

  <div class="section">
    <h2>SWOT Analysis</h2>
    <div class="swot">
      ${swotHTML(swot.strengths, 's', 'Strengths')}
      ${swotHTML(swot.weaknesses, 'w', 'Weaknesses')}
      ${swotHTML(swot.opportunities, 'o', 'Opportunities')}
      ${swotHTML(swot.threats, 't', 'Threats')}
    </div>
  </div>

  ${peers.length > 0 ? `
  <div class="section">
    <h2>Peer Comparison</h2>
    <div style="font-size:11px;color:#737373;margin-bottom:8px;">
      ${peers.length} peer group${peers.length > 1 ? 's' : ''} ${peerMode === 'strict' ? 'within ±25% revenue and ±20% lot count' : '(relaxed band — ±25% revenue only)'}
    </div>
    <table>
      <thead><tr>
        <th>Group</th><th>Property</th><th>Manager</th>
        <th class="right">Revenue</th><th class="right">Rev / Lot</th><th class="right">Margin</th><th class="right">Gross Margin</th>
        <th class="right">Lots</th><th class="center">NPS</th><th class="center">Meetings</th>
      </tr></thead>
      <tbody>${peerRowsHTML}</tbody>
    </table>
  </div>` : ''}

  ${npsComments.length > 0 ? `
  <div class="section">
    <h2>Verbatim Client Feedback (${npsComments.length})</h2>
    ${npsComments.map(c => {
      const cls = c.score >= 9 ? 'p' : c.score >= 7 ? 'pa' : 'd';
      return `<div class="verbatim ${cls}">
        <div class="v-meta"><span>${c.planNumber}</span><span>Score: ${c.score}</span></div>
        "${c.comment.replace(/"/g, '&quot;')}"
      </div>`;
    }).join('')}
  </div>` : ''}

  <div class="footer">
    TK Analytics · Plan Group Deep Dive Report · ${formatDateAU(new Date())} · CONFIDENTIAL
  </div>
</body>
</html>`;
};

export const exportAsHTML = (data) => {
  const html = buildHTMLDocument(data);
  const safeName = (data.target.propertyName || data.target.basePlan).replace(/[^a-z0-9-]+/gi, '-');
  triggerDownload(new Blob([html], { type: 'text/html;charset=utf-8' }), `deep-dive-${safeName}-${formatDateAU(new Date()).replace(/\//g, '-')}.html`);
};

export const exportAsPDF = (data) => {
  const html = buildHTMLDocument(data);
  // Use a hidden iframe + the browser's native print dialog (save as PDF).
  // Produces selectable-text PDFs with the same quality as the on-screen page.
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    }, 1500);
  };

  // Write the HTML directly into the iframe document. document.open/write/close
  // is more reliable than srcdoc across browsers + headless contexts.
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    console.error('Unable to access iframe document');
    cleanup();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  // Wait a tick so the print stylesheet + layout finalize, then print.
  setTimeout(() => {
    try {
      const win = iframe.contentWindow;
      if (win) {
        win.focus();
        win.onafterprint = cleanup;
        win.print();
      }
    } catch (err) {
      console.error('PDF print failed:', err);
    }
    setTimeout(cleanup, 30000); // safety net if user cancels
  }, 250);
};
